import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';

/** A live LEO Snak (Adtralza) shown as one short story, screen by screen. */
const SNAK_WALKTHROUGH = 'https://cdn.magicpatterns.com/uploads/h7bdnvhxLhSgFubkvT1rw7/Screenshot_2026-09-21_at_11.48.50.png';
const PRINCIPLES = ['Short enough to finish.', 'Interesting enough to continue.', 'Interactive enough to learn from.'] as const;
const ARC = ['Story', 'Choice', 'Insight', 'Action'] as const;
export function SlideAttentionProblem({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-67" data-mp="2clirupe4wtjh">Every interaction creates a signal.</SlideBanner>} data-id="element-66" data-mp="27spitpbmuz1l">
      <div className="flex flex-shrink-0 flex-col gap-[clamp(1rem,2vw,2rem)] lg:flex-row lg:items-start lg:justify-between lg:gap-12" data-id="element-68" data-mp="1f64r2nr8wkxi">
        <div className="min-w-0 lg:max-w-[42%]" data-id="element-69" data-mp="1k3n3wp8dvkas">
          <motion.p initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="mb-3 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-70" data-mp="3slg1dbd329g4">
            The attention problem
          </motion.p>

          <motion.h2 initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.12,
          duration: 0.55
        }} className="text-[clamp(1.8rem,3.4vw,3.2rem)] font-extrabold uppercase leading-[0.98] tracking-[-0.045em] text-[var(--fg)]" data-id="element-71" data-mp="3eszv81ovxvaz">
            HCPs don’t lack content.
            <span className="mt-1 block text-[var(--brand)]" data-id="element-72" data-mp="2zyljmm7nho6c">
              They lack time.
            </span>
          </motion.h2>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28,
        duration: 0.5
      }} className="max-w-2xl flex-shrink-0 border-l-2 border-[var(--brand)] pl-5" data-id="element-73" data-mp="y5oxa38aiz6x">
          <p className="text-[clamp(0.65rem,0.9vw,0.8rem)] font-bold uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-74" data-mp="2nkpy2nl8nsw5">
            So we created the Snak.
          </p>
          <p className="mt-1.5 text-[clamp(1.15rem,1.8vw,1.75rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-75" data-mp="2enfl8xsrvvp4">
            A short story you participate in.
          </p>
          <p className="mt-[clamp(0.5rem,0.9vw,0.8rem)] text-[clamp(0.8rem,1.05vw,1rem)] leading-relaxed text-[var(--muted)]" data-id="element-76" data-mp="36ebw5sv9vq9r">
            A Snak turns complex content into a short, engaging journey. HCPs
            don’t just read or watch — they choose, react and discover. Every
            interaction creates insight.
          </p>
          <ul className="mt-[clamp(0.6rem,1.1vw,1rem)] flex flex-wrap gap-x-[clamp(1rem,2vw,2rem)] gap-y-2" data-id="element-77" data-mp="32d9yvhkx1oaf">
            {PRINCIPLES.map((principle, index) => <li key={principle} className="flex items-center gap-2" data-id="element-78" data-mp="1qxv2og0j70gd" data-mpi={index}>
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--brand)] text-[10px] font-bold text-white" data-id="element-79" data-mp="1iehy91cmvihn">
                  {index + 1}
                </span>
                <span className="text-[clamp(0.78rem,1vw,0.95rem)] font-semibold text-[var(--fg)]" data-id="element-80" data-mp="1sce7fnm0g3d4">
                  {principle}
                </span>
              </li>)}
          </ul>
        </motion.div>
      </div>

      <motion.figure initial={{
      opacity: 0,
      y: 26
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4,
      duration: 0.6
    }} className="mt-[clamp(1rem,2vw,1.75rem)] flex min-h-0 flex-1 flex-col" data-id="element-81" data-mp="zp1k3b80zrir">
        <figcaption className="mb-[clamp(0.4rem,0.8vw,0.7rem)] flex-shrink-0 text-[clamp(0.58rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-82" data-mp="14o4qayzqs3sb">
          A live LEO Snak — one short story, screen by screen
        </figcaption>

        <div className="flex min-h-0 flex-1 items-center justify-center" data-id="element-83" data-mp="byxhn76kpxtc">
          <div className="relative aspect-[3.06/1] max-h-full w-full overflow-hidden rounded-[1.5rem] bg-white shadow-sm" data-id="element-84" data-mp="3be9er05987s9">
            <img src={SNAK_WALKTHROUGH} alt="Five consecutive screens from a live Adtralza Snak" className="absolute left-0 top-0 w-full -translate-y-[16%]" data-id="element-85" data-mp="3gcjbvgajhigi" />
          </div>
        </div>

        <div className="mt-[clamp(0.6rem,1.1vw,1rem)] flex flex-shrink-0 items-center justify-center gap-[clamp(0.5rem,1.2vw,1.25rem)]" data-id="element-86" data-mp="3u3y50fy5v01a">
          {ARC.map((step, index) => <React.Fragment key={step} data-id="element-87" data-mp="2z7wk3vwn856" data-mpi={index}>
              {index > 0 && <ArrowRightIcon className="h-[clamp(0.85rem,1.1vw,1.1rem)] w-[clamp(0.85rem,1.1vw,1.1rem)] flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-88" data-mp="7wzouz0gow9w" />}
              <span className="text-[clamp(0.75rem,1.05vw,1rem)] font-extrabold uppercase tracking-[0.18em] text-[var(--fg)]" data-id="element-89" data-mp="29q782jj73y12">
                {step}
              </span>
            </React.Fragment>)}
        </div>
      </motion.figure>
    </InteriorSlide>;
}